import React, { useState, useEffect } from "react";
import rubriquesService from "../../service/rubriques.service";
import articleService from "../../service/article.service";
import auteurService from "../../service/auteur.service";
import Swal from "sweetalert2";
import {
  StepCombined,
  StepAuthors,
  StepFiles,
  Step4Content,
  Step5Summary,
  Step3Bibliography,
} from "./edit/steps";
import StepIndicator from "./edit/StepIndicator";
import AffiliationServiceInstance from "../../service/affiliation.service";

// Constantes pour les formats de l'éditeur
const EDITOR_FORMATS = [
  "header",
  "font",
  "size",
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "list",
  "bullet",
  "indent",
  "link",
  "image",
  "color",
  "background",
  "align",
  "code-block",
  "script",
];

const EDITOR_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ indent: "-1" }, { indent: "+1" }],
    [{ color: [] }, { background: [] }],
    [{ align: [] }],
    ["link", "image"],
    ["clean"],
  ],
};

const EditArticlePage = ({ article, onClose, onSuccess }) => {
  // États
  const [formData, setFormData] = useState({
    title_art: {
      fr: "",
      en: "",
      mg: "",
    },
    resume_art: {
      fr: "",
      en: "",
      mg: "",
    },
    key_words_art: {
      fr: [],
      en: [],
      mg: [],
    },
    corp_art: {
      fr: "",
      en: "",
      mg: "",
    },
    study_area_art: "",
    id_rub: [], // Changé en array pour supporter react-select multi
    id_aut: 0,
    author: null, // Données complètes de l'auteur
    coauthor_ids: "", // IDs des co-auteurs séparés par virgules
    coauthors: [], // Données complètes des co-auteurs
    new_author_data: null, // Données du nouvel auteur si créé
    new_coauthors_data: [], // Données des nouveaux co-auteurs si créés
    file_art: null,
    doi_art: "En cours d'attribution",
    materiel_rech_art: "",
    bibliography_art: "",
  });

  // État pour le texte en cours de saisie des mots-clés
  const [keywordInputs, setKeywordInputs] = useState({
    fr: "",
    en: "",
    mg: "",
  });

  // États pour les utilisateurs
  const [authors, setAuthors] = useState([]);
  const [affiliations, setAffiliations] = useState([]);
  const [loadingAuthors, setLoadingAuthors] = useState(false);
  const [loadingAffiliations, setLoadingAffiliations] = useState(false);

  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 6; // Augmenté de 5 à 6
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rubriques, setRubriques] = useState([]);
  const [loadingRubriques, setLoadingRubriques] = useState(false);
  const [activeTab, setActiveTab] = useState({
    title: "fr",
    resume: "fr",
    keywords: "fr",
    content: "fr",
  });
  const [previewContent, setPreviewContent] = useState(false);
  const [wordCounts, setWordCounts] = useState({
    fr: 0,
    en: 0,
    mg: 0,
  });

  // Constante pour les champs requis
  const requiredField = <span className="ml-1 text-red-500">*</span>;
  // Charger les affiliations au montage du composant
  useEffect(() => {
    const fetchAffiliations = async () => {
      setLoadingAffiliations(true);
      try {
        const result = await AffiliationServiceInstance.getAll(1, 10000);
        // Correction importante : utiliser result.data.data
        setAffiliations(result.data.data);
      } catch (error) {
        console.error('Erreur lors du chargement des affiliations:', error);
      } finally {
        setLoadingAffiliations(false);
      }
    };

    fetchAffiliations();
  }, []);
  // Chargement des rubriques
  useEffect(() => {
    const fetchRubriques = async () => {
      setLoadingRubriques(true);
      try {
        const response = await rubriquesService.getAll();
        if (Array.isArray(response)) {
          setRubriques(response);
        } else if (
          response &&
          response.success &&
          Array.isArray(response.data)
        ) {
          setRubriques(response.data);
        } else {
          console.error("Unexpected rubriques data format:", response);
          setRubriques([]);
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
        setRubriques([]);
      } finally {
        setLoadingRubriques(false);
      }
    };

    fetchRubriques();
  }, []);

  useEffect(() => {
    const fetchAuthors = async () => {
      setLoadingAuthors(true);
      try {
        const response = await auteurService.getAll(10000); // Limite à 100 auteurs
        console.log("📋 Auteurs chargés:", {
          count: response.length,
          user: "HiQaody",
          timestamp: new Date().toISOString(),
        });
        setAuthors(response);
      } catch (error) {
        console.error("❌ Error fetching authors:", error);
        setAuthors([]);
      } finally {
        setLoadingAuthors(false);
      }
    };

    fetchAuthors();
  }, []);

  // Fonction de recherche d'auteurs - Modifiée pour utiliser auteurService
  const handleSearchAuthors = async (searchTerm) => {
    setLoadingAuthors(true);
    try {
      const result = await auteurService.search(searchTerm);
      setAuthors(result.data || []);
    } catch (error) {
      console.error('Erreur lors de la recherche d\'auteurs:', error);
    } finally {
      setLoadingAuthors(false);
    }
  };

  // Initialisation des données de l'article (reste largement identique)
  useEffect(() => {
    if (article) {
      const coauthorIds = article.coauthors
        ? article.coauthors.map((author) => author.id_aut).join(",")
        : "";

      let articleRubriques = [];
      if (article.rubriques && Array.isArray(article.rubriques)) {
        articleRubriques = article.rubriques.map((rub) => rub.id_rub);
      } else if (article.id_rub) {
        articleRubriques = [article.id_rub];
      }

      setFormData((prev) => ({
        ...prev,
        title_art: {
          fr: article.title_art?.fr || "",
          en: article.title_art?.en || "",
          mg: article.title_art?.mg || "",
        },
        resume_art: {
          fr: article.resume_art?.fr || "",
          en: article.resume_art?.en || "",
          mg: article.resume_art?.mg || "",
        },
        key_words_art: {
          fr: Array.isArray(article.key_words_art?.fr)
            ? article.key_words_art.fr
            : [],
          en: Array.isArray(article.key_words_art?.en)
            ? article.key_words_art.en
            : [],
          mg: Array.isArray(article.key_words_art?.mg)
            ? article.key_words_art.mg
            : [],
        },
        corp_art: {
          fr: article.corp_art?.fr || "",
          en: article.corp_art?.en || "",
          mg: article.corp_art?.mg || "",
        },
        study_area_art: article.study_area_art || "",
        id_rub: articleRubriques,
        id_aut: article.id_aut || 0,
        author: article.author || null,
        coauthor_ids: coauthorIds,
        coauthors: article.coauthors || [],
        file_art: null,
        doi_art: article.doi_art || "",
        materiel_rech_art: article.materiel_rech_art || "",
        research_material_url: article.research_material_url || "",
        bibliography_art: article.bibliography_art || "",
      }));

      setKeywordInputs({
        fr: "",
        en: "",
        mg: "",
      });

      console.log("📄 Article initialisé pour édition:", {
        articleId: article.id_art,
        authorId: article.id_aut,
        coauthorsCount: article.coauthors?.length || 0,
        user: "HiQaody",
        timestamp: new Date().toISOString(),
      });
    }
  }, [article]);
  // Calcul du nombre de mots
  useEffect(() => {
    const countWords = (text) => {
      if (!text) return 0;
      return text.trim().split(/\s+/).filter(Boolean).length;
    };

    setWordCounts({
      fr: countWords(formData.resume_art.fr),
      en: countWords(formData.resume_art.en),
      mg: countWords(formData.resume_art.mg),
    });
  }, [formData.resume_art]);

  // Gestionnaires d'événements avec logging
  const handleInputChange = (field, language, value) => {
    if (language) {
      setFormData((prev) => ({
        ...prev,
        [field]: {
          ...prev[field],
          [language]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  // Gestionnaire spécifique pour les rubriques (react-select)
  const handleRubriqueChange = (selectedOptions) => {
    const selectedIds = selectedOptions
      ? selectedOptions.map((option) => option.value)
      : [];
    handleInputChange("id_rub", null, selectedIds);
  };

  // Gestionnaire CORRIGÉ pour les mots-clés avec conversion automatique en tags
  const handleKeywordsChange = (language, inputValue) => {
    // Mettre à jour le champ de saisie
    setKeywordInputs((prev) => ({
      ...prev,
      [language]: inputValue,
    }));

    // Si l'utilisateur tape une virgule, convertir en tags
    if (inputValue.includes(",")) {
      const parts = inputValue.split(",");
      const newKeywords = parts
        .slice(0, -1) // Tous sauf le dernier
        .map((keyword) => keyword.trim())
        .filter((keyword) => keyword !== "");

      const remainingInput = parts[parts.length - 1]; // Le dernier segment après la dernière virgule

      if (newKeywords.length > 0) {
        // Ajouter les nouveaux mots-clés aux existants
        setFormData((prev) => {
          const currentKeywords = Array.isArray(prev.key_words_art[language])
            ? prev.key_words_art[language]
            : [];

          // Éviter les doublons
          const uniqueNewKeywords = newKeywords.filter(
            (keyword) => !currentKeywords.includes(keyword)
          );

          const updatedKeywords = [...currentKeywords, ...uniqueNewKeywords];

          return {
            ...prev,
            key_words_art: {
              ...prev.key_words_art,
              [language]: updatedKeywords,
            },
          };
        });

        // Mettre à jour le champ de saisie avec le texte restant
        setKeywordInputs((prev) => ({
          ...prev,
          [language]: remainingInput.trim(),
        }));
      }
    }
  };

  // Gestionnaire pour supprimer un mot-clé
  const handleRemoveKeyword = (language, indexToRemove) => {
    setFormData((prev) => {
      const currentKeywords = Array.isArray(prev.key_words_art[language])
        ? prev.key_words_art[language]
        : [];

      const updatedKeywords = currentKeywords.filter(
        (_, index) => index !== indexToRemove
      );

      return {
        ...prev,
        key_words_art: {
          ...prev.key_words_art,
          [language]: updatedKeywords,
        },
      };
    });
  };

  // Gestionnaire pour finaliser l'ajout d'un mot-clé (Entrée ou perte de focus)
  const handleKeywordFinalize = (language) => {
    const currentInput = keywordInputs[language]?.trim();

    if (currentInput && currentInput !== "") {
      setFormData((prev) => {
        const currentKeywords = Array.isArray(prev.key_words_art[language])
          ? prev.key_words_art[language]
          : [];

        // Éviter les doublons
        if (!currentKeywords.includes(currentInput)) {
          return {
            ...prev,
            key_words_art: {
              ...prev.key_words_art,
              [language]: [...currentKeywords, currentInput],
            },
          };
        }
        return prev;
      });

      // Vider le champ de saisie
      setKeywordInputs((prev) => ({
        ...prev,
        [language]: "",
      }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0] || null;
    setFormData((prev) => ({
      ...prev,
      file_art: file,
    }));
  };

  // Fonction pour synchroniser les langues entre tous les champs
  const handleTabChange = (section, lang) => {
    setActiveTab((prev) => ({
      ...prev,
      title: lang,
      resume: lang,
      keywords: lang,
      [section]: lang,
    }));
  };

  // Validation avec logging - Mise à jour pour 6 étapes
  const isCurrentStepValid = () => {
    let isValid = false;
    let missingFields = [];

    switch (currentStep) {
      case 1: // Infos principales
        if (!formData.title_art.fr.trim()) missingFields.push("Titre (FR)");
        if (!formData.id_rub || formData.id_rub.length === 0)
          missingFields.push("Au moins une catégorie");
        if (!formData.resume_art.fr.trim()) missingFields.push("Résumé (FR)");
        if (wordCounts.fr > 300)
          missingFields.push("Résumé trop long (>300 mots)");
        if (
          !Array.isArray(formData.key_words_art.fr) ||
          formData.key_words_art.fr.length === 0
        )
          missingFields.push("Mots-clés (FR)");

        isValid = missingFields.length === 0;
        break;
      case 2: // Auteurs
        if (!formData.id_aut || formData.id_aut === 0) missingFields.push("Auteur principal");
        isValid = missingFields.length === 0;
        break;
      case 3: // Fichiers & Matériel
        isValid = true;
        break;
      case 4: // Contenu
        isValid = true;
        break;
      case 5: // Référencement
        isValid = true;
        break;
      case 6: // Finalisation
        isValid = true;
        break;
      default:
        isValid = true;
    }

    return isValid;
  };

  // Navigation avec logging
  const nextStep = () => {
    if (isCurrentStepValid()) {
      if (currentStep < totalSteps) {
        setCurrentStep(currentStep + 1);
      }
    } else {
      Swal.fire({
        title: "Champs obligatoires",
        text: "Veuillez remplir tous les champs obligatoires avant de continuer.",
        icon: "warning",
        confirmButtonColor: "#57A863",
      });
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Utilitaires pour les rubriques
  const getRubriqueName = (id) => {
    const rubrique = rubriques.find((rub) => rub.id_rub === parseInt(id));
    return rubrique ? rubrique.name_rub : `ID: ${id}`;
  };

  // Fonction pour obtenir les noms des rubriques sélectionnées
  const getSelectedRubriqueNames = () => {
    if (!formData.id_rub || formData.id_rub.length === 0) {
      return "Aucune catégorie sélectionnée";
    }
    return formData.id_rub.map((id) => getRubriqueName(id)).join(", ");
  };

  // Préparer les options pour react-select
  const rubriqueOptions = rubriques.map((rubrique) => ({
    value: rubrique.id_rub,
    label: rubrique.name_rub,
  }));

  // Obtenir les valeurs sélectionnées pour react-select
  const getSelectedRubriqueOptions = () => {
    if (!formData.id_rub || formData.id_rub.length === 0) {
      return [];
    }
    return formData.id_rub
      .map((id) => {
        const rubrique = rubriques.find((rub) => rub.id_rub === id);
        return rubrique
          ? {
            value: rubrique.id_rub,
            label: rubrique.name_rub,
          }
          : null;
      })
      .filter(Boolean);
  };

  // Gestionnaires pour l'étape bibliographie
  const handleBibliographyNext = () => {
    nextStep();
  };

  const handleBibliographyPrevious = () => {
    prevStep();
  };

  // Soumission avec métadonnées complètes
  // Soumission avec métadonnées complètes - CORRIGÉE
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation finale
    if (
      !formData.title_art.fr ||
      !formData.id_rub ||
      formData.id_rub.length === 0 ||
      !formData.resume_art.fr ||
      !Array.isArray(formData.key_words_art.fr) ||
      formData.key_words_art.fr.length === 0 ||
      !formData.id_aut ||
      formData.id_aut === 0
    ) {
      Swal.fire({
        title: "Champs incomplets",
        text: "Veuillez remplir tous les champs obligatoires.",
        icon: "error",
        confirmButtonColor: "#57A863",
      });
      return;
    }

    // Confirmation avec informations détaillées
    const confirmResult = await Swal.fire({
      title: "Confirmer les modifications",
      html: `
    <div class="text-left">
      <p>Êtes-vous sûr de vouloir mettre à jour cet article ?</p>
      <ul class="mt-3 space-y-1 text-sm">
        <li><b>Titre :</b> ${formData.title_art.fr}</li>
        <li><b>Auteur :</b> ${formData.author ? `${formData.author.first_name} ${formData.author.last_name}` : 'Non défini'}</li>
        <li><b>Co-auteurs :</b> ${formData.coauthors ? formData.coauthors.length : 0}</li>
        <li><b>Catégories :</b> ${getSelectedRubriqueNames()}</li>
        <li><b>Mots-clés (FR) :</b> ${formData.key_words_art.fr.join(", ")}</li>
        <li><b>Nouveau fichier :</b> ${formData.file_art
          ? "Oui (" + formData.file_art.name + ")"
          : "Non (fichier existant conservé)"
        }</li>
        <li><b>Bibliographie :</b> ${formData.bibliography_art ? "Présente" : "Absente"
        }</li>
      </ul>
    </div>
  `,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#57A863",
      cancelButtonColor: "#d33",
      confirmButtonText: "Oui, mettre à jour",
      cancelButtonText: "Annuler",
    });

    if (!confirmResult.isConfirmed) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const submitData = new FormData();

      // === CHAMPS OBLIGATOIRES ===

      // 1. Titre (JSON stringifié)
      submitData.append("title_art", JSON.stringify(formData.title_art));

      // 2. Auteur principal (ID seul)
      submitData.append("id_aut", formData.id_aut.toString());

      // 3. Co-auteurs (IDs séparés par virgules ou chaîne vide)
      const coauthorIds = formData.coauthor_ids || "";
      if (coauthorIds) {
        submitData.append("id_co_aut", coauthorIds);
      }

      // 4. Rubriques (IDs séparés par virgules)
      const rubriqueIds = Array.isArray(formData.id_rub)
        ? formData.id_rub.join(",")
        : formData.id_rub.toString();
      submitData.append("id_rub", rubriqueIds);

      // 5. Résumé (JSON stringifié)
      submitData.append("resume_art", JSON.stringify(formData.resume_art));

      // 6. Mots-clés (JSON stringifié)
      submitData.append("key_words_art", JSON.stringify(formData.key_words_art));

      // 7. Contenu corporel (JSON stringifié)
      submitData.append("corp_art", JSON.stringify(formData.corp_art));

      // === CHAMPS OPTIONNELS ===

      // 8. Zone d'étude
      if (formData.study_area_art) {
        submitData.append("study_area_art", formData.study_area_art);
      }

      // 9. DOI
      if (formData.doi_art) {
        submitData.append("doi_art", formData.doi_art);
      }

      // 10. Matériel de recherche
      if (formData.materiel_rech_art) {
        submitData.append("materiel_rech_art", formData.materiel_rech_art);
      }

      // 11. URL matériel de recherche
      if (formData.research_material_url) {
        submitData.append("research_material_url", formData.research_material_url);
      }

      // 12. Bibliographie
      if (formData.bibliography_art) {
        submitData.append("bibliography_art", formData.bibliography_art);
      }

      // === FICHIER (si présent) ===
      if (formData.file_art && formData.file_art instanceof File) {
        submitData.append("file_art", formData.file_art);
      } else {
        // Envoyer un champ file_art vide si pas de nouveau fichier
        submitData.append("file_art", "");
      }

      // === MÉTADONNÉES DE NOUVEAUX AUTEURS (si présents) ===
      if (formData.new_author_data) {
        submitData.append("new_author_data", JSON.stringify(formData.new_author_data));
      }

      if (formData.new_coauthors_data && formData.new_coauthors_data.length > 0) {
        submitData.append("new_coauthors_data", JSON.stringify(formData.new_coauthors_data));
      }

      // === ENVOI DE LA REQUÊTE ===
      const response = await articleService.update(article.id_art, submitData);

      await Swal.fire({
        title: "Article mis à jour !",
        html: `
      <div class="text-left">
        <p>Les modifications ont été enregistrées avec succès.</p>
        <div class="mt-3 text-sm text-gray-600">
          <p><b>Auteur :</b> ${formData.author ? `${formData.author.first_name} ${formData.author.last_name}` : 'Non défini'}</p>
          <p><b>Co-auteurs :</b> ${formData.coauthors ? formData.coauthors.length : 0}</p>
          <p><b>Catégories :</b> ${getSelectedRubriqueNames()}</p>
          <p><b>Mots-clés (FR) :</b> ${formData.key_words_art.fr.join(", ")}</p>
          <p><b>Fichier :</b> ${formData.file_art && formData.file_art instanceof File
            ? `Nouveau fichier téléchargé (${formData.file_art.name})`
            : "Fichier existant conservé"
          }</p>
        </div>
      </div>
    `,
        icon: "success",
        confirmButtonColor: "#57A863",
      });

      onSuccess && onSuccess(article.id_art);
      onClose && onClose();
    } catch (err) {

      setError("Une erreur est survenue lors de la mise à jour de l'article.");

      await Swal.fire({
        title: "Erreur",
        text: `Une erreur est survenue lors de la mise à jour de l'article: ${err.message || 'Erreur inconnue'}`,
        icon: "error",
        confirmButtonColor: "#d33",
      });
    } finally {
      setLoading(false);
    }
  };

  // Rendu
  return (
    <div className="container mx-auto mt-8">
      <div className="px-6 pt-6">
        <StepIndicator
          currentStep={currentStep}
          totalSteps={totalSteps}
          loading={loading}
          setCurrentStep={setCurrentStep}
        />
      </div>

      <div className="px-6 py-6">
        <form onSubmit={(e) => e.preventDefault()}>
          {currentStep === 1 && (
            <StepCombined
              formData={formData}
              keywordInputs={keywordInputs}
              handleInputChange={handleInputChange}
              handleKeywordsChange={handleKeywordsChange}
              handleRemoveKeyword={handleRemoveKeyword}
              handleKeywordFinalize={handleKeywordFinalize}
              handleRubriqueChange={handleRubriqueChange}
              activeTab={activeTab}
              handleTabChange={handleTabChange}
              wordCounts={wordCounts}
              rubriques={rubriques}
              rubriqueOptions={rubriqueOptions}
              selectedRubriqueOptions={getSelectedRubriqueOptions()}
              loadingRubriques={loadingRubriques}
              requiredField={requiredField}
            />
          )}

          {currentStep === 2 && (
            <StepAuthors
              formData={formData}
              handleInputChange={handleInputChange}
              authors={authors}
              loadingAuthors={loadingAuthors}
              onSearchAuthors={handleSearchAuthors}
              affiliations={affiliations} // Nouveau prop
              loadingAffiliations={loadingAffiliations} // Nouveau prop
              requiredField={requiredField}
            />
          )}

          {currentStep === 3 && (
            <StepFiles
              formData={formData}
              handleFileChange={handleFileChange}
              handleInputChange={handleInputChange}
              article={article}
            />
          )}

          {currentStep === 4 && (
            <Step4Content
              formData={formData}
              handleInputChange={handleInputChange}
              activeTab={activeTab}
              handleTabChange={handleTabChange}
              previewContent={previewContent}
              setPreviewContent={setPreviewContent}
              EDITOR_MODULES={EDITOR_MODULES}
              EDITOR_FORMATS={EDITOR_FORMATS}
            />
          )}

          {currentStep === 5 && (
            <Step3Bibliography
              formData={formData}
              handleInputChange={handleInputChange}
              onNext={handleBibliographyNext}
              onPrevious={handleBibliographyPrevious}
            />
          )}

          {currentStep === 6 && (
            <Step5Summary
              formData={formData}
              error={error}
              article={article}
              loadingRubriques={loadingRubriques}
              getRubriqueName={getRubriqueName}
              getSelectedRubriqueNames={getSelectedRubriqueNames}
            />
          )}
        </form>
      </div>

      {/* Navigation buttons */}
      <div className="flex justify-between border-t bg-gray-50 px-6 py-4">
        <div>
          {currentStep > 1 && currentStep !== 5 && (
            <button
              type="button"
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#57A863] focus:ring-offset-2"
              onClick={prevStep}
              disabled={loading}
            >
              Précédent
            </button>
          )}
        </div>

        <div className="flex gap-3">
          <button
            type="button"
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#57A863] focus:ring-offset-2"
            onClick={onClose}
            disabled={loading}
          >
            Annuler
          </button>

          {currentStep < totalSteps && currentStep !== 5 ? (
            <button
              type="button"
              className={`rounded-md bg-[#57A863] px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-[#4a9154] focus:outline-none focus:ring-2 focus:ring-[#57A863] focus:ring-offset-2 ${!isCurrentStepValid() ? "cursor-not-allowed opacity-70" : ""
                }`}
              onClick={nextStep}
              disabled={!isCurrentStepValid() || loading}
            >
              Suivant
            </button>
          ) : currentStep === totalSteps ? (
            <button
              type="button"
              className={`rounded-md px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-[#57A863] focus:ring-offset-2 ${loading
                ? "cursor-not-allowed bg-indigo-400"
                : "bg-[#57A863] hover:bg-[#4a9154]"
                }`}
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? (
                <span className="flex items-center">
                  <svg
                    className="-ml-1 mr-2 h-4 w-4 animate-spin text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Mise à jour en cours...
                </span>
              ) : (
                "Mettre à jour l'article"
              )}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default EditArticlePage;